"use client";

import { useEffect, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  LockKeyhole,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import Link from "next/link";
import { trackEvent } from "@/lib/analytics/posthog";
import { waitlistSchema, type WaitlistFormData } from "@/schemas/waitlist";

const ACCESS_INTENTS = [
  {
    value: "validate-idea",
    title: "I am validating a real idea right now",
    description: "I want evidence before I commit more time to building.",
  },
  {
    value: "unlock-custom",
    title: "I want custom niche access",
    description: "I need the gated path because starter niches are not enough.",
  },
  {
    value: "founding-access",
    title: "I want founding access before launch",
    description: "I want the launch pricing and early workflow advantage.",
  },
] as const;

export interface WaitlistModalProps {
  triggerText?: string;
  className?: string;
  analyticsSource?: string;
  analyticsContext?: {
    nicheSlug?: string;
    resultKind?: string;
  };
  /**
   * When provided, the dialog becomes controlled — the parent owns
   * open/close state. Omit both `open` and `onOpenChange` for the
   * default uncontrolled (trigger-button) behaviour.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Optional callback fired after the waitlist form is successfully submitted.
   * Useful for analytics or follow-up actions in the guided demo flow.
   */
  onSubmitSuccess?: () => void;
  /** When true the built-in DialogTrigger button is hidden (controlled mode). */
  hideTrigger?: boolean;
}

export function WaitlistModal({
  triggerText = "Apply for founding access",
  className,
  analyticsSource = "waitlist_modal",
  analyticsContext,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onSubmitSuccess,
  hideTrigger = false,
}: WaitlistModalProps) {
  const [step, setStep] = useState<"qualify" | "email">("qualify");
  const [selectedIntent, setSelectedIntent] = useState<string>("");
  const [confirmedIntent, setConfirmedIntent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState(
    "Something went wrong. Please try again.",
  );
  const [internalOpen, setInternalOpen] = useState(false);
  const dialogContentId = useId();

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled
    ? (v: boolean) => controlledOnOpenChange?.(v)
    : setInternalOpen;

  const {
    register,
    handleSubmit: registerHandleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<WaitlistFormData>({
    resolver: zodResolver(waitlistSchema),
  });

  useEffect(() => {
    if (!open) {
      reset();
      setStep("qualify");
      setSelectedIntent("");
      setConfirmedIntent(false);
      setSubmissionStatus("idle");
      setAcceptedTerms(false);
    }
  }, [open, reset]);

  const onSubmit = async (data: WaitlistFormData) => {
    if (!acceptedTerms) return;

    trackEvent("lead_capture_submitted", {
      flow: "waitlist_modal",
      source: analyticsSource,
      niche_slug: analyticsContext?.nicheSlug,
      result_kind: analyticsContext?.resultKind,
      intent: selectedIntent || undefined,
    });

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        // Handle server-side validation errors from Zod
        if (errorData?.details) {
          setErrorMessage(errorData.details[0]?.message || "Invalid email");
          setSubmissionStatus("error");
          return;
        }
        throw new Error(errorData?.error || "Failed to request early access");
      }

      setSubmissionStatus("success");
      trackEvent("lead_capture_completed", {
        flow: "waitlist_modal",
        source: analyticsSource,
        niche_slug: analyticsContext?.nicheSlug,
        result_kind: analyticsContext?.resultKind,
        intent: selectedIntent || undefined,
      });
      onSubmitSuccess?.();

      // Close modal after a delay on success
      setTimeout(() => {
        setOpen(false);
      }, 3000);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to request early access";
      setErrorMessage(message);
      setSubmissionStatus("error");
    }
  };

  const canContinue = selectedIntent.length > 0 && confirmedIntent;

  function handleContinue() {
    if (!canContinue) return;
    setStep("email");
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <Button
          type="button"
          size="lg"
          aria-controls={dialogContentId}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen(true)}
          className={`h-12 w-full gap-2 bg-accent px-6 text-base font-semibold text-accent-foreground shadow-lg shadow-accent/20 transition-all hover:bg-accent/90 hover:shadow-xl hover:shadow-accent/25 sm:h-14 sm:w-auto sm:px-8 ${className ?? ""}`}
        >
          {triggerText}
          <ArrowRight className="size-4" aria-hidden="true" />
        </Button>
      )}
      <DialogContent
        id={dialogContentId}
        className="mx-4 max-h-[90vh] max-w-[calc(100%-2rem)] overflow-y-auto p-5 sm:mx-0 sm:max-w-md sm:p-6"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Apply for founding access
          </DialogTitle>
          <DialogDescription>
            Reserved for founders who want pre-launch access, custom niche
            coverage, and founding pricing.
          </DialogDescription>
        </DialogHeader>

        {submissionStatus === "success" ? (
          <div className="flex flex-col items-center justify-center px-2 py-6 text-center sm:px-4 sm:py-8">
            <div className="mb-4 rounded-full bg-emerald-100 p-3 text-emerald-600 dark:bg-emerald-900/20">
              <CheckCircle2 className="size-7 sm:size-8" />
            </div>
            <h3 className="text-base font-semibold text-foreground sm:text-lg">
              Access request received
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              We&apos;ll be in touch soon with your early access invitation.
            </p>
          </div>
        ) : step === "qualify" ? (
          <div className="space-y-6 pt-4">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-accent/12 text-accent">
                  <LockKeyhole className="size-4" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    This is a higher-intent path
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    We keep this application slightly stricter on purpose. If
                    you continue, you&apos;re signalling genuine interest in
                    using Holmes before launch.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                What best describes why you want access?
              </p>
              <RadioGroup
                value={selectedIntent}
                onValueChange={setSelectedIntent}
                className="gap-3"
              >
                {ACCESS_INTENTS.map((intent) => (
                  <label
                    key={intent.value}
                    htmlFor={intent.value}
                    className="flex cursor-pointer flex-row items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 transition hover:border-accent/30 hover:bg-accent/[0.06] sm:flex-nowrap"
                  >
                    <RadioGroupItem
                      id={intent.value}
                      value={intent.value}
                      className="mt-0.5 size-4 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold leading-snug text-foreground">
                        {intent.title}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                        {intent.description}
                      </p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="intent-confirmation"
                checked={confirmedIntent}
                onCheckedChange={(checked) =>
                  setConfirmedIntent(checked === true)
                }
                className="mt-1"
              />
              <label
                htmlFor="intent-confirmation"
                className="text-sm leading-relaxed text-muted-foreground"
              >
                I&apos;m actively exploring this problem space and want access
                because I expect to use the product, not just browse it.
              </label>
            </div>

            <div className="sticky bottom-0 -mx-1 border-t border-white/10 bg-background/95 px-1 pt-4 pb-1 backdrop-blur supports-[backdrop-filter]:bg-background/85">
              <Button
                type="button"
                onClick={handleContinue}
                disabled={!canContinue}
                className="w-full gap-2 bg-accent py-4 text-sm font-semibold text-accent-foreground shadow-lg shadow-accent/20 transition-all hover:bg-accent/90 focus-visible:ring-2 focus-visible:ring-accent/50 sm:py-6 sm:text-base"
              >
                Continue to application
                <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={registerHandleSubmit(onSubmit)}
            className="space-y-6 pt-4"
          >
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-accent/12 text-accent">
                  <Sparkles className="size-4" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Final step
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    Leave your email to be considered for pre-launch access and
                    founding pricing.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium text-foreground"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="name@example.com"
                {...register("email")}
                className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
              {errors.email && (
                <p className="text-sm font-medium text-red-500">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) =>
                  setAcceptedTerms(checked === true)
                }
                className="mt-1"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="terms"
                  className="text-sm text-muted-foreground leading-relaxed"
                >
                  I have read and accept the{" "}
                  <Link
                    href="/legal/terms"
                    className="text-accent underline hover:text-accent/80"
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link
                    href="/legal/privacy"
                    className="text-accent underline hover:text-accent/80"
                  >
                    Privacy Policy
                  </Link>
                  .
                </label>
              </div>
            </div>

            {submissionStatus === "error" && (
              <p className="text-sm font-medium text-red-500">{errorMessage}</p>
            )}

            <button
              type="button"
              onClick={() => setStep("qualify")}
              className="text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
            >
              Go back and adjust your application
            </button>

            <div className="sticky bottom-0 -mx-1 border-t border-white/10 bg-background/95 px-1 pt-4 pb-1 backdrop-blur supports-[backdrop-filter]:bg-background/85">
              <Button
                type="submit"
                disabled={isSubmitting || !acceptedTerms}
                className="w-full gap-2 bg-accent py-4 text-sm font-semibold text-accent-foreground shadow-lg shadow-accent/20 transition-all hover:bg-accent/90 focus-visible:ring-2 focus-visible:ring-accent/50 sm:py-6 sm:text-base"
              >
                {isSubmitting ? (
                  <Loader2 className="size-4 shrink-0 animate-spin" />
                ) : (
                  <>
                    Submit application
                    <ArrowRight
                      className="size-4 shrink-0"
                      aria-hidden="true"
                    />
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
